import { Link } from "react-router-dom";
import { Scale, Phone, Mail, ArrowRight } from "lucide-react";
import { SITE } from "@/lib/site";

const sections = [
  {
    id: "publisher",
    title: "1. Website Publisher",
    body: [
      "This website is published and operated by BackHome Restoration Inc., providing 24/7 emergency restoration support, contents assessment, inventory management, pack-out and pack-back, storage solutions and professional moving services across Toronto and the Greater Toronto Area.",
    ],
    list: [
      "BackHome Restoration Inc. · Toronto & the Greater Toronto Area · Canada",
    ],
  },
  {
    id: "contact",
    title: "2. Contact Information",
    list: [
      `Emergency line (24/7): ${SITE.phone}`,
      `Claims & enquiries: ${SITE.email}`,
      "Service area: Toronto, Mississauga, Brampton, Vaughan, Markham, Richmond Hill, Ajax, Pickering, Whitby, Oshawa, Burlington, Oakville and surrounding communities.",
      "All written enquiries should be directed through the Contact page on this website or by email.",
    ],
  },
  {
    id: "ip",
    title: "3. Intellectual Property",
    body: [
      "All content on this website, including the BackHome logo, brand name, wordmark, page copy, photography, illustrations, graphics, layouts and code, is the exclusive property of BackHome Restoration Inc. or used under licence from its rightful owners.",
      "No element of this website may be reproduced, distributed, modified, transmitted, republished, displayed or used for commercial purposes without prior written authorization from BackHome Restoration Inc. Unauthorized use may infringe copyright, trademark and other applicable laws.",
    ],
  },
  {
    id: "trademarks",
    title: "4. Trademarks",
    body: [
      "BackHome, BackHome Restoration, the BackHome house-and-swirl logo and associated slogans (including \"Restoring more than property, restoring peace of mind\") are trademarks of BackHome Restoration Inc. All other trademarks shown on this website remain the property of their respective owners.",
    ],
  },
  {
    id: "use",
    title: "5. Use of the Website",
    body: [
      "This website is provided for informational purposes. By accessing it you agree not to:",
    ],
    list: [
      "Use the website in any unlawful, fraudulent or harmful manner.",
      "Attempt to gain unauthorized access to servers, accounts, data or related infrastructure.",
      "Disrupt the operation of the website or interfere with other users' access.",
      "Scrape, copy or reproduce content in bulk for commercial use without consent.",
    ],
  },
  {
    id: "content",
    title: "6. Accuracy of Content",
    body: [
      "We make reasonable efforts to keep the content on this website accurate and up to date. However, information is provided on an \"as is\" basis without warranty of any kind. Service availability, response times and scope may vary based on the nature of the emergency, location and operational conditions.",
      "Information displayed on this website does not constitute a binding offer or guarantee. A formal scope of work, estimate or contract will be provided in writing before services are performed.",
    ],
  },
  {
    id: "liability",
    title: "7. Limitation of Liability",
    body: [
      "To the maximum extent permitted by applicable law, BackHome Restoration Inc., its affiliates, officers and employees shall not be liable for any direct, indirect, incidental, consequential or punitive damages arising from the use of, or inability to use, this website, including any reliance placed on information found here.",
      "Emergency response should never be delayed waiting on a website. Always call the 24/7 emergency line for time-sensitive situations.",
    ],
  },
  {
    id: "links",
    title: "8. External Links",
    body: [
      "This website may contain links to third-party websites operated by partners, regulators or industry bodies. We do not control and are not responsible for the content, accuracy or practices of any third-party site. Visiting external sites is at your own risk.",
    ],
  },
  {
    id: "law",
    title: "9. Governing Law",
    body: [
      "This Legal Notice and any use of this website are governed by the laws of the Province of Ontario, Canada, and the federal laws of Canada applicable therein. Any dispute shall be subject to the exclusive jurisdiction of the courts of Ontario, except where otherwise required by mandatory law.",
    ],
  },
  {
    id: "updates",
    title: "10. Updates to this Notice",
    body: [
      "BackHome Restoration Inc. may revise this Legal Notice at any time without prior notice. The current version, with its effective date, will always be available on this page.",
    ],
  },
];

export default function LegalNotice() {
  return (
    <div data-testid="page-legal" className="bg-[hsl(var(--gaza-paper))]">
      {/* HERO */}
      <section className="relative bg-[hsl(var(--gaza-teal-deep))] text-white overflow-hidden" data-testid="legal-hero">
        <div className="grain absolute inset-0" aria-hidden />
        <div className="absolute top-0 left-0 right-0 h-1 ember-stripe siren-sweep" />
        <div className="container-gaza relative py-24 md:py-32">
          <div className="live-pill mb-8 reveal-up" data-reveal>
            <Scale className="w-3.5 h-3.5" />
            <span>Terms · Trademarks · Conditions of use</span>
          </div>
          <h1 className="display-chunky text-5xl md:text-7xl lg:text-[6rem] leading-[0.95] max-w-4xl reveal-up" data-reveal data-reveal-delay="120">
            Legal <span className="text-[hsl(var(--gaza-crimson-bright))]">Notice</span>
          </h1>
          <p className="text-lg md:text-xl text-white/80 max-w-2xl leading-relaxed mt-8 reveal-up" data-reveal data-reveal-delay="240">
            Plain-language terms covering how this website is published, what you can do with its content, and the rules that frame your visit.
          </p>
          <div className="text-[11px] uppercase tracking-[0.28em] text-white/55 font-extrabold mt-10 reveal-up" data-reveal data-reveal-delay="320">
            Effective date: February 2026
          </div>
        </div>
      </section>

      {/* BODY */}
      <section className="container-gaza py-20 md:py-28 grid md:grid-cols-12 gap-12" data-testid="legal-body">
        {/* Table of contents */}
        <aside className="md:col-span-4 lg:col-span-3">
          <div className="md:sticky md:top-24">
            <div className="eyebrow mb-4">On this page</div>
            <ul className="space-y-2 text-sm">
              {sections.map((s) => (
                <li key={s.id}>
                  <a href={`#${s.id}`} className="text-[hsl(var(--gaza-teal-mist))] hover:text-[hsl(var(--gaza-crimson))] font-semibold transition" data-testid={`legal-toc-${s.id}`}>
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>
            <div className="mt-10 p-6 border border-[hsl(var(--gaza-line))] bg-white">
              <div className="text-[11px] tracking-[0.28em] uppercase text-[hsl(var(--gaza-crimson))] font-extrabold mb-3">Questions?</div>
              <p className="text-sm text-[hsl(var(--gaza-teal-mist))] leading-relaxed mb-4">Legal or licensing matters can be routed through our coordination team.</p>
              <a href={SITE.phoneHref} className="inline-flex items-center gap-2 text-[hsl(var(--gaza-teal))] font-extrabold text-sm" data-testid="legal-phone">
                <Phone className="w-4 h-4 text-[hsl(var(--gaza-crimson))]" /> {SITE.phone}
              </a>
            </div>
          </div>
        </aside>

        {/* Sections */}
        <div className="md:col-span-8 lg:col-span-9 space-y-14">
          {sections.map((s) => (
            <article key={s.id} id={s.id} data-reveal className="reveal-up scroll-mt-28" data-testid={`legal-section-${s.id}`}>
              <h2 className="display-chunky text-3xl md:text-4xl text-[hsl(var(--gaza-teal))] leading-tight mb-5">
                {s.title}
              </h2>
              {s.body?.map((p) => (
                <p key={p} className="text-[hsl(var(--gaza-teal-mist))] leading-relaxed text-base md:text-lg mb-4">{p}</p>
              ))}
              {s.list && (
                <ul className="space-y-3 mt-4">
                  {s.list.map((item) => (
                    <li key={item} className="flex gap-3 text-[hsl(var(--gaza-teal-mist))] leading-relaxed text-base md:text-lg">
                      <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--gaza-crimson))] mt-2.5 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </article>
          ))}

          {/* Contact card */}
          <div className="border border-[hsl(var(--gaza-line))] bg-white p-8 md:p-10" data-testid="legal-contact-card">
            <div className="flex items-start justify-between gap-6 flex-wrap">
              <div>
                <div className="eyebrow mb-3">Legal contact</div>
                <h3 className="display-chunky text-3xl text-[hsl(var(--gaza-teal))] leading-tight mb-2">BackHome Restoration Inc.</h3>
                <p className="text-[hsl(var(--gaza-teal-mist))] leading-relaxed max-w-md">Serving Toronto & the Greater Toronto Area. For trademark, copyright or content-use enquiries, route requests through our coordination team.</p>
              </div>
              <div className="flex flex-col gap-3">
                <a href={SITE.phoneHref} className="btn-primary" data-testid="legal-contact-call"><Phone className="w-4 h-4" /> {SITE.phone}</a>
                <Link to="/contact" className="btn-outline-dark" data-testid="legal-contact-link"><Mail className="w-4 h-4" /> Send a Message <ArrowRight className="w-4 h-4" /></Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
